import api from '@/services/api'
import axios from 'axios'
import Cookies from 'js-cookie'
import router from '@/router'
import { useUserStore } from '@/stores/user-store'
import config from '@/services/config'

const authAxios = axios.create()
const baseURL = config.baseURL
authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const { useToast } = await import('vuestic-ui')
      const toast = useToast()
      toast.init({
        message: 'Your session has expired. Please log in again.',
        color: 'danger',
      })
      // Reset user store before routing
      const { useUserStore } = await import('@/stores/user-store')
      const userStore = useUserStore()
      userStore.logout()
      router.replace({ name: 'login' })
    }
    return Promise.reject(error)
  },
)

// Extract token from cookie
function getCookie(name: string) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

// Set defaults
authAxios.defaults.withCredentials = true
authAxios.defaults.headers.common['X-XSRF-TOKEN'] = getCookie('XSRF-TOKEN')
authAxios.defaults.headers.common['Accept'] = 'application/json'

authAxios.interceptors.request.use((config) => {
  const token = getCookie('XSRF-TOKEN')
  if (token) {
    config.headers['X-XSRF-TOKEN'] = token
  }
  return config
})

export async function login(credentials: { email: string; password: string }) {
  const userStore = useUserStore()

  // Get CSRF cookie
  await authAxios.get(`${baseURL}/sanctum/csrf-cookie`, {
    withCredentials: true,
  })

  // Get the token from the cookie and set it as a header
  const xsrfToken = Cookies.get('XSRF-TOKEN')

  const response = await authAxios.post(`${baseURL}/login`, credentials, {
    withCredentials: true,
    headers: {
      'X-XSRF-TOKEN': decodeURIComponent(xsrfToken ?? ''),
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    maxRedirects: 0,
    validateStatus: (status) => status < 400,
  })

  if (response.status === 204 || response.status === 200) {
    userStore.setUser(response.data.user)
    router.push('/dashboard')
  }
}

export { authAxios }

export async function logout() {
  const userStore = useUserStore()
  await authAxios.post(
    `${baseURL}/logout`,
    {},
    {
      withCredentials: true,
    },
  )
  userStore.logout()
  router.push({ name: 'login' })
}
